
import { of, BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as _ from 'lodash';
import moment from 'moment';
import { jwtDecode } from 'jwt-decode';
import ms from 'ms';

// Api
import {
	ApiModule,
	AuthenticateService,
	Configuration,
	ConfigurationParameters,
	Auth,
	User,
	Action,
	ActionSearch,
	ActionFind
} from 'papi-client-ng2';

// Services
import { PapiDbService } from './db-service';

import { StorageService } from '../auth/_services';

// Environment
import { environment } from '../../environments/environment';

export interface ApiModelFind<Model> {
	count?: number;
	rows?: Array<Model>;
}

export interface ApiModelSearch {
	/**
	 * A list of the attributes that you want to return.
	 */
	attributes?: Array<any>;
	/**
	 * A hash of attributes to describe your search.
	 */
	where?: any;
	/**
	 * A collection of grouped attribute names and directions.
	 */
	order?: Array<Array<string>>;
	/**
	 * The number of Organization instances to return.
	 */
	limit?: number;
	/**
	 * The number of Organization instances to offset the returned rows.
	 */
	offset?: number;
}

export class Organization {
	public static ROOT: string = '1';
	public static PUBLIC: string = '2';
};

@Injectable()
export class Service {
	public authenticate: AuthenticateService;
	public db: PapiDbService;
	public user: User;
	public remember: boolean = true;

	private configuration: Configuration;
	private apiKeys: { [key: string]: string };

	private email: string;
	private password: string;

	private actions: Action[] = null;

	private static paginateLimit = 1000;

	private refreshTimer = null;

	constructor(private router: Router, protected httpClient: HttpClient, protected storage: StorageService) {
		this.storage.init(environment?.authkeys?.PAA_AUTH_TOKEN);		// see if we can initialize off of a stored value

		let currentAuth: Auth = JSON.parse(this.storage.instance.getItem(environment?.authkeys?.PAA_AUTH_TOKEN));

		if (!_.isNil(currentAuth)) {
			this.setAuth(currentAuth);
			this.configure();
			this.setAuthenticate();
			this.setDb();
		}
	}

	/**
	 * Set the authenticate api.
	 */
	private setAuthenticate() {
		this.authenticate = new AuthenticateService(this.httpClient, environment.PAPI_BASE_PATH, this.configuration);
	}

	/**
	 * Set the DB api.
	 */
	private setDb() {
		this.db = new PapiDbService(this.httpClient, environment.PAPI_BASE_PATH, this.configuration);
	}

	/**
	 * Automatically paginates all records into a collection (array) of results and wraps it as a find model.
	 *
	 * @param search - The search model.
	 * @param context - The API service to to request.
	 * @param method - The method to call.
	 */
	public findAutoPaginate<Model, ModelSearch, ModelFind>(
		search: ModelSearch,
		context: AuthenticateService | PapiDbService,
		method: ((ModelSearch, observe?: string, reportProgress?: boolean) => Observable<ModelFind>)
	): Observable<ApiModelFind<Model>> {
		return this.autoPaginate<Model, ModelSearch, ModelFind>(search, context, method).pipe(
			map((rows: Model[]) => {
				return <ApiModelFind<Model>>{
					count: _.size(rows),
					rows: rows
				};
			})
		);
	}

	/**
	 * Automatically paginates all records into a collection (array) of results.
	 *
	 * @param search - The search model.
	 * @param context - The API service to to request.
	 * @param method - The method to call.
	 */
	public autoPaginate<Model, ModelSearch, ModelFind>(
		search: ModelSearch,
		context: AuthenticateService | PapiDbService,
		method: ((ModelSearch, observe?: string, reportProgress?: boolean) => Observable<ModelFind>)
	): Observable<Model[]> {
		// bind context to callback method
		method = method.bind(context);

		// create
		return <Observable<Model[]>>Observable.create((observer) => {
			// init
			let collection: Model[] = [];

			// find
			method(search, 'body', true).subscribe((result: ApiModelFind<Model>) => {
				// concat
				collection = _.concat(collection, result.rows);

				// set
				const count: number = result.count;
				const rowsLength: number = result.rows.length;

				// see if we should continue
				if (rowsLength < count) {
					// init
					const taskCount = Math.ceil((count - rowsLength) / Service.paginateLimit);

					// loop to create tasks
					const observables = _.map(_.range(0, taskCount), (task) => {
						// init
						const limit = Service.paginateLimit;
						const offset = (rowsLength + (Service.paginateLimit * task));

						// merge into page parameters
						const pageSearch: ModelSearch = <ModelSearch>_.merge({}, search, {
							offset: offset,
							limit: limit
						});

						return method(pageSearch, 'body', true);
					});

					// join
					forkJoin(...observables).subscribe((results: ApiModelFind<Model>[]) => {
						// concat
						collection = _.concat(collection, ...(_.map(results, 'rows')));

						// finish
						observer.next(collection);
						observer.complete();
					});
				} else {
					// finish
					observer.next(collection);
					observer.complete();
				}
			});
		});
	}

	public fauxInit() {
		this.email = '';
		this.password = '';
		this.apiKeys = { Authorization: '' };

		this.configure();
		this.setAuthenticate();
		this.setDb();
	}

	public tokenLogin(token: any): Observable<Auth> {
		let auth: Auth = {
			token: token.id_token
		};

		this.email = '';
		this.password = '';
		this.apiKeys = { Authorization: auth.token };
		this.configure();
		this.setAuthenticate();
		this.setDb();
		return of(auth);
	}

	/**
	 * Login
	 */
	public login(email: string, password: string): Observable<Auth> {

		// set
		this.email = email;
		this.password = password;
		
		// configure
		this.configure();
		this.setAuthenticate();

		return this.authenticate.login('body').pipe(map((auth: Auth) => {
			// set
			this.setAuth(auth);
			this.configure();
			this.setAuthenticate();
			this.setDb();

			return auth;
		}));
	}

	/**
	 * Authorize against stored actions
	 */
	public authorize(key: string): Observable<boolean> {
		const search = {
			attributes: ['id', 'code', 'description'],
			where: {
				code: {
					$iLike: 'arcadmin%'
				}
			}
		} as ActionSearch;
		const observer = _.isArray(this.actions) ? of({count: this.actions.length, rows: this.actions} as ActionFind) : this.db.getActionSession(search, 'body', true);

		return observer.pipe(map((data: ActionFind) => {
			this.actions = data.rows;
			return !!_.find(this.actions, (action) => {
				return action.code == key;
			});
		}));
	}

	/**
	 * Authorize against cached actions
	 */
	public isAuthorized(key: string): boolean {
		return !!_.find(this.actions, (action) => {
			return action.code == key;
		});
	}

	/**
	 * Verifies the user session.
	 */
	public verify(): Observable<boolean> {
		if (!_.isNil(this.db)) {
			return this.db.getUserSession(undefined, 'body').pipe(catchError(() => {
				return of(false);
			})).pipe(map((user: User) => {
				// set
				this.user = user;			

				if (user) {
					return true;
				}

				return false;
			}));
		}

		return of(false);
	}

	/**
	 * Logout
	 */
	public logout() {
		// set
		this.authenticate = undefined;
		this.db = undefined;

		this.apiKeys = undefined;

		this.email = undefined;
		this.password = undefined;
		this.user = undefined;

		this.actions = null;

		// configure
		this.configure();

		// remove current user
		this.storage.instance.removeItem(environment.authkeys.PAA_AUTH_TOKEN);
	}

	private setAuth(auth: Auth) {
		// store user details and jwt token in local storage to keep user logged in between page refreshes
		this.storage.instance.setItem(environment.authkeys.PAA_AUTH_TOKEN, JSON.stringify(auth));

		// set the api keys
		this.apiKeys = { Authorization: auth.token };

		// schedule refresh token
		this.schedulePapiRefreshToken(auth.token);
	}

	private configure() {
		const params: ConfigurationParameters = {
			username: this.email,
			password: this.password,
			apiKeys: this.apiKeys
		}

		this.configuration = new Configuration(params);
	}

	private navigateToLogin(): Observable<boolean> {
		// invalidate
		this.fauxInit();
		this.router.navigate(['/login'], {});
		return of(false);
	}

	private schedulePapiRefreshToken(token): Observable<boolean> {
		if(!token || !this.apiKeys || !this.apiKeys.Authorization){
			return this.navigateToLogin();
		}

		// get sec to next expiration
		let secRemaining = this.secUntilExpire(token);
		let secEarly = 15; // start early before the prev times out
		let secNext = secRemaining - secEarly;
		secNext = secNext <= 0 ? 0 : secNext;

		if(secRemaining <= 0){
			return this.navigateToLogin();
		}


		// if the user refreshes the page we can get two of these going.  only allow one.
		// Hmm,  there was a case in ArcLive where two timers were going.  It may not be relevant in ArcAdmin.
		if(this.refreshTimer){
			clearTimeout(this.refreshTimer);
		}

		this.refreshTimer = setTimeout(() => {
			// get refresh token from papi
			return this.getPapiRefreshToken().subscribe((auth: Auth) => {

				return this.reverifyPermissionForArcAdmin().subscribe((hasPermission) => {
					if(hasPermission){
						// save token and schedule next refresh
						this.setAuth(auth);
						this.configure();
						this.setAuthenticate();
						this.setDb();

						return true;
					}
					return false;
				});
			});
		}, secNext * 1000);
	}

	// call papi to get a refreshed token
	private getPapiRefreshToken(): Observable<Auth>{
		// configure
		this.configure();
		this.setAuthenticate();
		
		// call papi to get a fresh token
		return this.authenticate.refresh('body')
			.pipe(map((auth: Auth) => {
				return auth;
			}));
	}

	private secUntilExpire(token){
		try{
			var decoded: any = jwtDecode(token);
			var len = this.parseExpiresInToMilliseconds(decoded.expiresIn); // ms
			var expAt = decoded.iat + (len / 1000); // s + s
			var now = moment().unix(); // s
			var remaining = expAt - now; // s - s
			return remaining;
		}catch(error){
			// error / not valid
			return  -1;
		}
	}

	private parseExpiresInToMilliseconds(expiresIn){
		let milliseconds = null;
		if(typeof expiresIn === 'string'){
			milliseconds = ms(expiresIn);
		}else{
			milliseconds = expiresIn * 1000;
		}
		return milliseconds;
	}
	
	/**
	 * After getting a refresh token, we need to reverify user permission to use ArcAdmin.
	 */
	private reverifyPermissionForArcAdmin(): Observable<boolean>{
		// invalidate
		this.actions = null;

		// requery for new permissions
		return this.authorize('arcadmin').pipe(map((hasPermission: boolean) => {
			if(!hasPermission){
				// set to not logged in
				this.navigateToLogin();
				return false;
			}
			return true;
		}));
	}
}
